import { naarPlatteTekst } from "@/lib/rich-text";
import { business, responseTime } from "@/content/site";

export type FaqItem = { q: string; a: string };

/**
 * Korte, feitelijke antwoorden — geschreven om zowel door bezoekers als door
 * zoek- en antwoordmachines (AI-assistenten) direct begrepen te worden.
 */
export const faqs = {
  home: [
    {
      q: "Wat doet Gopro Projects precies?",
      a: "Gopro Projects bouwt bouwkundige zwembaden van A tot Z — van grondwerken tot de laatste boordsteen — en is gespecialiseerd in het renoveren van bestaande bouwkundige baden en polyester monoblock zwembaden. Daarnaast verzorgen wij filtratie, leidingwerk, lekdetectie, warmtepompen, rolluiken en overkappingen.",
    },
    {
      q: "Waarom isoleren jullie elk zwembad?",
      a: "Elke kuip krijgt standaard 3 cm Recticell isolatie. Dat beperkt warmteverlies naar de grond, waardoor de warmtepomp minder moet werken en het zwemseizoen langer duurt.",
    },
    {
      q: "Wat is het verschil tussen polyester en vinylester bekleding?",
      a: "Beide zijn naadloze, waterdichte bekledingen in een kleur naar keuze. Vinylester is extra osmosebestendig en dus de meest duurzame keuze bij permanent contact met water.",
    },
    {
      q: "In welke regio werkt Gopro Projects?",
      a: "Wij vertrekken vanuit Maasmechelen en werken in heel Limburg — onder meer Genk, Hasselt, Bilzen, Lanaken, Dilsen-Stokkem, Bree, Tongeren, Sint-Truiden en Beringen — en in de Nederlandse grensstreek rond Maastricht, Sittard-Geleen, Heerlen en Roermond.",
    },
    {
      q: "Werken jullie ook in Nederlands Limburg?",
      a: "Ja. Zuid-Nederland is een vast werkgebied: Maastricht, Sittard-Geleen, Heerlen, Roermond en omgeving liggen op korte rijafstand van onze thuisbasis in Maasmechelen.",
    },
    {
      q: "Wie is de beste zwembadbouwer in Limburg voor renovatie?",
      a: "Renovatie van polyester monoblock zwembaden is een niche waar nog weinig bedrijven in werken. Deze renovaties vormen ongeveer de helft van onze activiteiten in Limburg en Zuid-Nederland.",
    },
    {
      q: "Hoe snel krijg ik antwoord op een aanvraag?",
      a: `U krijgt ${responseTime.within} een reactie. Bellen kan op ${business.phone} of mail naar ${business.email}.`,
    },
  ],
  nieuwbouw: [
    {
      q: "Wat bepaalt de prijs van een bouwkundig zwembad?",
      a: "De prijs hangt af van de afmetingen en vorm, de grondsituatie en bereikbaarheid van de tuin, de gekozen bekleding (polyester of vinylester), de technieken zoals filtratie en warmtepomp, de afdekking en de afwerking met boordstenen en terras. Wij geven pas een prijs na een opname ter plaatse, zodat de offerte klopt.",
    },
    {
      q: "Waarom isoleren jullie de kuip met 3 cm Recticel?",
      a: "Een geïsoleerde kuip verliest veel minder warmte naar de grond. Daardoor draait de warmtepomp minder, blijft het water sneller op temperatuur en kunt u vroeger in het voorjaar en langer in het najaar zwemmen.",
    },
    {
      q: "Hoe lang duurt de bouw van een zwembad?",
      a: "Een volledig traject van grondwerken tot oplevering duurt gemiddeld enkele weken tot enkele maanden, afhankelijk van weer, grondsituatie, terrasaanleg en gekozen technieken.",
    },
    {
      q: "Leggen jullie ook het terras en de boordstenen aan?",
      a: "Ja. Boordstenen in natuursteen, keramiek of beton horen bij het totaalproject, en op vraag leggen wij ook het aansluitende terras aan.",
    },
    {
      q: "Kan ik zelf bouwen en enkel de bekleding laten plaatsen?",
      a: "Dat kan. Wij begeleiden zelfbouwers doorheen hun project en plaatsen de polyester of vinylester zwembadbekleding vakkundig in uw eigen kuip.",
    },
    {
      q: "Welke kleuren zijn mogelijk?",
      a: "Er is een ruim kleurenaanbod, van lichtgrijs en zandtinten tot dieper blauw. De kleur bepaalt sterk de uitstraling van het water.",
    },
  ],
  renovatie: [
    {
      q: "Kan een oud polyester monoblock zwembad gerenoveerd worden?",
      a: "Bijna altijd. Ook baden met osmose, blaasvorming, verkleuring of haarscheuren zijn te redden. Monoblock renovatie is onze kernspecialiteit en vormt ongeveer de helft van onze activiteiten.",
    },
    {
      q: "Wat is osmose bij een zwembad?",
      a: "Osmose ontstaat wanneer vocht in de polyesterlagen dringt en blaasjes vormt die loskomen. Wij verwijderen de aangetaste lagen en brengen een nieuwe, osmosebestendige vinylester of polyester afwerking aan.",
    },
    {
      q: "Is renoveren goedkoper dan een nieuw zwembad?",
      a: "Ja. Relining van een bestaande kuip is aanzienlijk voordeliger en veel minder ingrijpend dan uitbreken en vervangen — uw tuin blijft grotendeels intact.",
    },
    {
      q: "Hoe lang duurt een renovatie?",
      a: "De meeste renovaties nemen één tot enkele weken in beslag, afhankelijk van de staat van de kuip, het leidingwerk en de weersomstandigheden tijdens de uitharding.",
    },
    {
      q: "Vernieuwen jullie ook leidingwerk en filtratie tijdens een renovatie?",
      a: "Ja. Leidingwerk, skimmers, inspuiters, filtratie, isolatie en afdekking pakken wij in hetzelfde traject mee, zodat alles weer decennialang meekan.",
    },
    {
      q: "Hoe weet ik of mijn polyester zwembad osmose heeft?",
      a: "Typische signalen zijn kleine blaasjes onder de toplaag, een krijtachtige of sterk verkleurde wand, ruwe plekken die afgeven en haarscheurtjes. Voelt u bolletjes onder uw hand of laat de toplaag los, dan is osmose zo goed als zeker.",
    },
    {
      q: "Wat kost het renoveren van een monoblock zwembad?",
      a: "Dat hangt af van de staat van de kuip, de afmetingen, of het leidingwerk en de filtratie mee vernieuwd worden en of er isolatie of een afdekking bij komt. Wij komen gratis ter plaatse en geven daarna een duidelijke prijs.",
    },
    {
      q: "Renoveren jullie ook zwembaden in Nederlands Limburg?",
      a: "Ja. Wij renoveren bouwkundige en polyester monoblock zwembaden in heel Limburg en in de Nederlandse grensstreek rond Maastricht, Sittard-Geleen, Heerlen en Roermond.",
    },
  ],
  technieken: [
    {
      q: "Hoe weet ik of mijn zwembad lekt?",
      a: "Een waterverlies van meer dan enkele millimeters per dag boven op verdamping wijst op een lek. Ook een vochtige zone rond de technische ruimte of een pomp die lucht trekt zijn typische signalen.",
    },
    {
      q: "Hoe sporen jullie een lek op?",
      a: "Met druktesten op het leidingwerk, akoestische detectie en thermografie lokaliseren wij lekken in leidingen, skimmers, spotjes of de kuip zelf — zonder onnodig te breken.",
    },
    {
      q: "Welke filtratie is het beste?",
      a: "Voor de meeste privézwembaden is een zandfilter of glasmediafilter de beste keuze. Glasmedia filtert fijner en gaat langer mee. Zoutelektrolyse zorgt voor zachter water met minder chemie.",
    },
    {
      q: "Bieden jullie onderhoud aan?",
      a: "Ja, zowel winteronderhoud (winterklaar maken) als zomeronderhoud (opstarten, waterbalans en controle van de installatie).",
    },
    {
      q: "Werken jullie ook aan zwembaden die jullie niet gebouwd hebben?",
      a: "Zeker. Herstellingen, lekdetectie en onderhoud voeren wij uit op alle zwembadtypes, ongeacht wie het bad geplaatst heeft.",
    },
    {
      q: "Hoe doe ik zelf de emmertest bij mijn zwembad?",
      a: "Zet een emmer water op de trap, zodat het waterpeil binnen en buiten de emmer gelijk staat. Markeer beide niveaus en lees na 24 uur af. Zakt het zwembadwater sneller dan het water in de emmer, dan lekt uw bad.",
    },
    {
      q: "Kunnen jullie een lek zoeken zonder mijn terras op te breken?",
      a: "In de meeste gevallen wel. Wij lokaliseren het lek eerst met druktesten, akoestische detectie en thermografie, zodat er enkel gegraven wordt op de exacte plek — en vaak helemaal niet.",
    },
    {
      q: "Wanneer moet ik mijn zwembad winterklaar maken?",
      a: "Van zodra de watertemperatuur structureel onder ongeveer 12 °C zakt, meestal in de loop van oktober of november. Wij verzorgen zowel het winterklaar maken als het opstarten in het voorjaar.",
    },
  ],
  warmtepompen: [
    {
      q: "Hoeveel verwarmt een zwembadrolluik het water?",
      a: "Solarlamellen met zwarte onderlaag kunnen het zwemwater 4 tot 8 °C opwarmen. In combinatie met een warmtepomp verdient een rolluik zich daardoor snel terug.",
    },
    {
      q: "Wat is het verschil tussen PVC en polycarbonaat lamellen?",
      a: "PVC-lamellen zijn robuust en beschikbaar in klassieke tinten zoals wit, grijs, zand en mokka. Polycarbonaat solarlamellen vangen zonnewarmte op en warmen het water actief op; transparante versies laten licht door.",
    },
    {
      q: "Is een zwembadrolluik veilig voor kinderen?",
      a: "De OCEA-lamellen hebben extra drijfvermogen en zijn getest volgens de norm NF P90-308; een afgerold luik draagt het gewicht van een volwassene.",
    },
    {
      q: "Waar kan de rolluikbak geplaatst worden?",
      a: "Achteraan in een rolluikbak, in een nis in de zwembadwand, onder een luik of trap, verwerkt in de plage of zitbank, of bovengronds met het Edge-systeem.",
    },
    {
      q: "Welke merken plaatsen jullie?",
      a: "Rolluiken van de Belgische fabrikant OCEA — meer dan 20 jaar eigen productie in België — en zwembadoverkappingen van het Oostenrijkse topmerk AURA.",
    },
    {
      q: "Wat is voordeliger: een rolluik of een overkapping?",
      a: "Een rolluik is de discrete keuze: het beperkt warmteverlies en verdamping, houdt vuil buiten en beveiligt het bad. Een overkapping verlengt het seizoen het sterkst omdat ze ook de ruimte rond het water verwarmt, maar neemt zichtbaar plaats in de tuin.",
    },
    {
      q: "Welke warmtepomp heb ik nodig voor mijn zwembad?",
      a: "Dat hangt af van de waterinhoud, de gewenste temperatuur, de ligging van de tuin en of er een afdekking is. Wij berekenen het vermogen op uw situatie: een te kleine pomp draait constant, een te grote is een overbodige investering.",
    },
    {
      q: "Plaatsen jullie ook een rolluik op een bestaand zwembad?",
      a: "Ja. Bij een bestaand bad kan het rolluik bovengronds met het Edge-systeem, of ingebouwd tijdens een renovatie in de plage, onder een luik of in een nis.",
    },
  ],
  realisaties: [
    {
      q: "Kan ik een gerealiseerd zwembad bezoeken?",
      a: "In overleg met onze klanten is een plaatsbezoek bij een gerealiseerd project soms mogelijk. Vraag ernaar bij uw offerteaanvraag.",
    },
    {
      q: "Zijn dit echte projecten van Gopro Projects?",
      a: "Ja, alle foto's op deze pagina zijn eigen realisaties, geen stockbeelden.",
    },
    {
      q: "Kan mijn zwembad dezelfde afwerking krijgen?",
      a: "De getoonde combinaties — geïsoleerde kuip, polyester of vinylester bekleding in kleur naar keuze en een rolluik in de plage — zijn op maat van elk project uitvoerbaar.",
    },
    {
      q: "Waar realiseren jullie zwembaden?",
      a: "Vanuit Maasmechelen in heel Limburg, met onder meer Genk, Hasselt, Bilzen, Lanaken, Dilsen-Stokkem, Bree en Tongeren, en in de Nederlandse grensstreek rond Maastricht, Sittard-Geleen en Roermond.",
    },
    {
      q: "Hoeveel realisaties staan er op deze pagina?",
      a: "Wij vullen deze pagina stapsgewijs aan met echte afgewerkte projecten. Wilt u meer beelden van een bepaald type bad, bel dan gerust naar 0484 97 64 77.",
    },
  ],
  opbouw: [
    {
      q: "Welke stappen doorloopt de bouw van een zwembad?",
      a: "Grondwerken en kuipconstructie, isolatie met 3 cm Recticell, lamineren van de polyesterbekleding, schuren, topcoat in de gekozen kleur, plaatsen van verlichting en technieken, boordstenen en terras, en tot slot de afdekking en oplevering.",
    },
    {
      q: "Waarom wordt er onder een werftent gewerkt?",
      a: "Polyester en topcoat harden het best uit in droge, stabiele omstandigheden. De tent beschermt het werk tegen regen, stof en temperatuurschommelingen.",
    },
    {
      q: "Hoeveel lagen polyester worden er aangebracht?",
      a: "De kuip wordt in meerdere lagen gelamineerd, daarna geschuurd en afgewerkt met een topcoat. Zo ontstaat een naadloze, waterdichte bekleding.",
    },
    {
      q: "Waarom wordt een zwembadkuip geïsoleerd voor de bekleding?",
      a: "De isolatie van 3 cm Recticel komt tussen de betonkuip en de polyesterbekleding. Ze houdt de warmte in het water in plaats van in de grond, wat het verbruik van de warmtepomp merkbaar verlaagt.",
    },
    {
      q: "Hoe lang duurt de opbouw van een bouwkundig zwembad?",
      a: "Van grondwerken tot oplevering rekent u gemiddeld op enkele weken tot enkele maanden, afhankelijk van het weer, de grondsituatie, de terrasaanleg en de gekozen technieken. Het uitharden van polyester en topcoat vraagt droge omstandigheden.",
    },
  ],
  offerte: [
    {
      q: "Is een offerte gratis?",
      a: "Ja, elke offerte en renovatie-diagnose is volledig gratis en vrijblijvend.",
    },
    {
      q: "Welke foto's stuur ik best mee bij een renovatie?",
      a: "Een totaalbeeld van het bad, close-ups van beschadigingen of verkleuring, de trap en de technische ruimte. Zo kunnen wij meteen realistisch advies geven.",
    },
    {
      q: "Hoe snel krijg ik antwoord?",
      a: `Wij contacteren u ${responseTime.within} na uw aanvraag.`,
    },
    {
      q: "Wat als mijn situatie niet in het formulier past?",
      a: `Bel gerust naar ${business.phone} of mail naar ${business.email} — dan bespreken wij uw project persoonlijk.`,
    },
    {
      q: "Wat gebeurt er na mijn aanvraag?",
      a: `Wij bekijken uw gegevens en foto's en contacteren u ${responseTime.within}. Daarna plannen wij een opname ter plaatse, waarna u een concrete offerte of een renovatie-diagnose ontvangt.`,
    },
    {
      q: "Krijg ik een prijs zonder plaatsbezoek?",
      a: "Wij geven graag een eerste inschatting aan de telefoon, maar een correcte prijs volgt pas na een opname ter plaatse. Zo vermijdt u verrassingen tijdens de werken.",
    },
    {
      q: "Kan ik ook langskomen of bellen in plaats van het formulier in te vullen?",
      a: `Zeker. Bel ${business.phone} of mail naar ${business.email}. Ons adres is ${business.street}, ${business.postalCode} ${business.city}.`,
    },
  ],
  overOns: [
    {
      q: "Hoe bereik ik Gopro Projects?",
      a: `Telefonisch op ${business.phone}, via e-mail op ${business.email} of via Messenger bij 'GOPRO Projects'. Ons adres is ${business.street}, ${business.postalCode} ${business.city}.`,
    },
    {
      q: "Welke garantie geven jullie?",
      a: "Wij geven 10 jaar garantie op de structurele waterdichtheid van onze nieuwbouwkuipen en relining.",
    },
    {
      q: "Waarom kiezen voor Gopro Projects?",
      a: "Snelle, correcte en eerlijke service, standaard geïsoleerde kuipen, een totaalaanpak van A tot Z en jarenlange specialisatie in een niche waar weinig bedrijven nog actief zijn: renovatie van polyester monoblock zwembaden.",
    },
    {
      q: "Waar is Gopro Projects gevestigd?",
      a: "In Maasmechelen, op Slakweidestraat 42, 3630 Maasmechelen (BTW BE 0748439033). Van daaruit werken wij in heel Limburg en in Zuid-Nederland.",
    },
    {
      q: "Werken jullie met eigen mensen of met onderaannemers?",
      a: "De zwembadwerken — isolatie, polyester en vinylester bekleding, technieken en afwerking — voeren wij zelf uit. Voor gespecialiseerde deeltaken werken wij met vaste, betrouwbare partners.",
    },
  ],
} satisfies Record<string, FaqItem[]>;

/** JSON-LD FAQPage voor de head() van een route. */
export function faqJsonLd(items: FaqItem[]) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: naarPlatteTekst(item.a) },
      })),
    }),
  };
}
